import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppState } from '$/store';
import { updateUser } from '$/store/user/actions';
import BixCheckbox from '$/components/global/bixCheckbox';
import BixToggle from '$/components/global/bixToggle';
import './index.scss';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import BixButton from '$/components/global/bixButton';

interface IUserDetailProps {
  currentUser?: Bixby.Models.User;
  isLoading: boolean;
  dispatchSetUser?: (user: Bixby.Models.UserSlim) => Promise<void>;
  dispatchUpdateUser: (updateUser: Bixby.Models.UpdateUserRequest) => Promise<void>;
}

const UserAccess: React.FC<IUserDetailProps> = props => {
  const { currentUser, dispatchUpdateUser } = props;
  const [userCompanies, setUserCompanies] = useState([] as Bixby.Models.UserCompany[]);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentUser && !Bixby.Utils.isNullOrEmpty(currentUser)) {
      const currentCompanies: Bixby.Models.UserCompany[] =
        currentUser && !Bixby.Utils.isNullOrEmpty(currentUser.companies) ? currentUser.companies : [];

      setUserCompanies(Bixby.Utils.orderBy(currentCompanies, c => c.company_Name));
    }
  }, [currentUser]);

  const handleSubmit = () => {
    if (currentUser && userCompanies) {
      setIsLoading(true);
      const updateUserRequest = {
        ...currentUser,
        companies: userCompanies,
      } as Bixby.Models.UpdateUserRequest;
      dispatchUpdateUser(updateUserRequest).finally(() => setIsLoading(false));
    }
  };

  const nameCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/company/${props.data.company_Id}`}>
      {props.data.company_Name}
    </Link>
  );

  const alertsCellRenderer = (props: any) => {
    const handleAlertsEnabled = (checked: boolean) => {
      props.data.alertsEnabled = checked;
      props.api.refreshCells();
    };
    return (
      <BixToggle
        id={`${props.data.company_Id}.alerts`}
        checked={props.data.alertsEnabled}
        onChange={handleAlertsEnabled}
      />
    );
  };

  const accessCellRenderer = (props: any) => {
    return (
      <BixCheckbox
        id={`${props.data.company_Id}/.access`}
        forGrid
        children
        checked={props.data.active}
        disabled={true}
      />
    );
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  return currentUser ? (
    <React.Fragment>
      <section className="user_company_access">
        <div className="grid_container">
          <div className="grid ag-theme-alpine">
            <AgGridReact
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                sortable: true,
                resizable: true,
                lockPinned: true,
                filter: 'agTextColumnFilter',
                cellClass: 'align-center',
              }}
              onGridReady={onGridReady}
              rowData={userCompanies}
              suppressDragLeaveHidesColumns={true}
              suppressMovableColumns={true}
              animateRows={true}
              rowHeight={35}
              headerHeight={35}
              components={{
                nameCellRenderer,
                alertsCellRenderer,
                accessCellRenderer,
              }}
              columnDefs={[
                {
                  field: 'company_Name',
                  headerName: 'Company',
                  cellClass: 'align-left',
                  minWidth: 50,
                  cellRenderer: 'nameCellRenderer',
                },
                {
                  field: 'sector_Name',
                  headerName: 'Sector',
                  cellClass: 'align-left',
                  minWidth: 100,
                },
                {
                  field: 'industryGroup_Name',
                  headerName: 'Industry Group',
                  cellClass: 'align-left',
                  minWidth: 100,
                  cellRenderer: 'companyNameCellRenderer',
                },
                {
                  field: 'alertsEnabled',
                  headerName: 'Send Alerts',
                  cellClass: 'align-left',
                  minWidth: 100,
                  cellRenderer: 'alertsCellRenderer',
                },
                {
                  field: 'active',
                  headerName: 'Provide Access',
                  cellClass: 'align-left',
                  minWidth: 100,
                  cellRenderer: 'accessCellRenderer',
                },
                {
                  headerName: 'Access Date',
                  field: 'accessDate',
                  cellRenderer: (params: any) => {
                    return params.value ? dayjs.utc(params.value).format('MM/DD/YYYY') : 'N/A';
                  },
                },
              ]}></AgGridReact>
            <div className="button_container extra_pad">
              <BixButton type="submit" onClick={handleSubmit} disabled={isLoading} useLoading isLoading={isLoading}>
                Save User
              </BixButton>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  return {
    currentUser: state.userState.currentUser,
    isLoading: state.userState.isLoading,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateUser: (user: Bixby.Models.UpdateUserRequest) => dispatch(updateUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserAccess);
